import React, { useState, useEffect } from 'react';
import { useTask } from '../contexts/TaskContext';

export default function BucketListSettingsModal({ isOpen, onClose }) {
  const { birthdate, setBirthdate, bucketItems, addBucket, updateBucket, deleteBucket } = useTask();

  const [bdLocal, setBdLocal] = useState('');
  const [newName, setNewName] = useState('');
  const [newYears, setNewYears] = useState('');
  const [newMonths, setNewMonths] = useState('0');

  // 編集中のアイテム
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editYears, setEditYears] = useState('');
  const [editMonths, setEditMonths] = useState('0');

  useEffect(() => {
    if (isOpen) {
      setBdLocal(birthdate || '');
      setEditingId(null);
    }
  }, [isOpen, birthdate]);

  if (!isOpen) return null;

  // 達成済みは「達成済み」タブで表示するためここでは除外し、若い目標年齢（targetYears * 12 + targetMonths）が小さい順に並べる
  const activeItems = bucketItems
    .filter((it) => !it.completed)
    .slice()
    .sort((a, b) => {
      const ay = (Number(a.targetYears) || 0) * 12 + (Number(a.targetMonths) || 0);
      const by = (Number(b.targetYears) || 0) * 12 + (Number(b.targetMonths) || 0);
      return ay - by;
    });

  const handleSaveBirthdate = async () => {
    if (!bdLocal) return;
    await setBirthdate(bdLocal);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    const y = parseInt(newYears, 10);
    if (isNaN(y) || y < 0 || y > 100) return;
    const m = parseInt(newMonths, 10) || 0;
    await addBucket({ name: newName.trim(), targetYears: y, targetMonths: m });
    setNewName('');
    setNewYears('');
    setNewMonths('0');
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditYears(String(item.targetYears || 0));
    setEditMonths(String(item.targetMonths || 0));
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async () => {
    if (!editName.trim()) return;
    const y = parseInt(editYears, 10);
    if (isNaN(y) || y < 0 || y > 100) return;
    const m = parseInt(editMonths, 10) || 0;
    await updateBucket(editingId, {
      name: editName.trim(),
      targetYears: y,
      targetMonths: m,
    });
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('この項目を削除しますか？')) return;
    await deleteBucket(id);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content bucket-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            <span aria-hidden="true">🎯</span> バケットリスト設定
          </h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* 生年月日 */}
          <section className="bucket-section">
            <h3 className="bucket-section-title">生年月日</h3>
            <div className="bucket-birthdate-row">
              <input
                type="date"
                className="bucket-birthdate-input"
                value={bdLocal}
                onChange={(e) => setBdLocal(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
              <button
                type="button"
                className="btn-primary bucket-birthdate-save"
                onClick={handleSaveBirthdate}
                disabled={!bdLocal || bdLocal === birthdate}
              >
                保存
              </button>
            </div>
            {!birthdate && (
              <p className="bucket-hint">残り日数の計算に使います。</p>
            )}
          </section>

          {/* 追加フォーム */}
          <section className="bucket-section">
            <h3 className="bucket-section-title">新しい目標を追加</h3>
            <form className="bucket-add-form" onSubmit={handleAdd}>
              <input
                type="text"
                className="bucket-add-name"
                placeholder="達成したいこと（例：世界一周）"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <div className="bucket-age-row">
                <label className="bucket-age-label">
                  <input
                    type="number"
                    className="bucket-age-input"
                    min="0"
                    max="100"
                    placeholder="35"
                    value={newYears}
                    onChange={(e) => setNewYears(e.target.value)}
                  />
                  <span>歳</span>
                </label>
                <label className="bucket-age-label">
                  <input
                    type="number"
                    className="bucket-age-input"
                    min="0"
                    max="11"
                    placeholder="0"
                    value={newMonths}
                    onChange={(e) => setNewMonths(e.target.value)}
                  />
                  <span>ヶ月</span>
                </label>
                <button
                  type="submit"
                  className="btn-primary bucket-add-btn"
                  disabled={!newName.trim() || newYears === ''}
                >
                  追加
                </button>
              </div>
            </form>
          </section>

          {/* 既存リスト */}
          <section className="bucket-section">
            <h3 className="bucket-section-title">登録済み（{activeItems.length}）</h3>
            {activeItems.length === 0 ? (
              <p className="bucket-hint">まだ項目がありません。</p>
            ) : (
              <ul className="bucket-edit-list">
                {activeItems.map((item) => (
                  <li key={item.id} className="bucket-edit-row">
                    {editingId === item.id ? (
                      <div className="bucket-edit-form">
                        <input
                          type="text"
                          className="bucket-add-name"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                        />
                        <div className="bucket-age-row">
                          <label className="bucket-age-label">
                            <input
                              type="number"
                              className="bucket-age-input"
                              min="0"
                              max="100"
                              value={editYears}
                              onChange={(e) => setEditYears(e.target.value)}
                            />
                            <span>歳</span>
                          </label>
                          <label className="bucket-age-label">
                            <input
                              type="number"
                              className="bucket-age-input"
                              min="0"
                              max="11"
                              value={editMonths}
                              onChange={(e) => setEditMonths(e.target.value)}
                            />
                            <span>ヶ月</span>
                          </label>
                          <button type="button" className="btn-primary" onClick={saveEdit}>
                            保存
                          </button>
                          <button type="button" className="btn-secondary" onClick={cancelEdit}>
                            キャンセル
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bucket-edit-display">
                        <span className="bucket-edit-name">{item.name}</span>
                        <span className="bucket-edit-age">
                          {item.targetYears}歳{item.targetMonths ? `${item.targetMonths}ヶ月` : ''}
                        </span>
                        <div className="bucket-edit-actions">
                          <button
                            type="button"
                            className="bucket-edit-btn"
                            onClick={() => startEdit(item)}
                            aria-label="編集"
                          >
                            編集
                          </button>
                          <button
                            type="button"
                            className="bucket-delete-btn"
                            onClick={() => handleDelete(item.id)}
                            aria-label="削除"
                          >
                            削除
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-primary" onClick={onClose}>
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}
